import React, {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import cloneDeep from 'lodash.clonedeep';
import { RootState } from 'data/root.reducers';
import { FormTemplate, FormTemplateSection } from 'data/template/template.types';
import * as actions from 'data/accessFunction/accessFunction.actions';
import {
  AccessFunctionFormData,
  AccessFunctionInterfaceFormSectionType,
  AccessFunctionInterfaceFormData,
  AccessFunctionFormSection,
} from 'data/accessFunction/accessFunction.types';
import { IpAddressFormData, ManualEntryFormData } from 'data/ipAssignment/ipAssignment.types';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import * as dfniSelectors from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.selectors';
import * as dfniActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as userSelectors from 'users/users-selectors';
import * as userActions from 'users/users-actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import * as networkSelectors from 'data/network/network.selectors';
import * as networkActions from 'data/network/network.actions';
import * as vpnConfigTemplateSelectors from 'data/vpnTemplate/vpnTemplate.selectors';
import * as vpnConfigTemplateActions from 'data/vpnTemplate/vpnTemplate.actions';
import * as vpnConfigSelectors from 'data/vpnConfig/vpnConfig.selectors';
import * as vpnConfigActions from 'data/vpnConfig/vpnConfig.actions';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as sshSecurityInfoSelectors from 'data/sshSecurityInfo/sshSecurityInfo.selectors';
import * as sshSecurityInfoActions from 'data/sshSecurityInfo/sshSecurityInfo.actions';
import * as distributorConfigSelectors from 'data/distributorConfiguration/distributorConfiguration.selectors';
import * as distributorConfigActions from 'data/distributorConfiguration/distributorConfiguration.actions';
import * as distributorConfigAdapters from 'data/distributorConfiguration/distributorConfiguration.adapter';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as locationSelectors from 'data/location/location.selectors';
import * as locationActions from 'data/location/location.actions';
import * as templateSelectors from 'data/template/template.selectors';
import * as xtpActions from 'data/xtp/xtp.actions';
import * as xtpSelectors from 'data/xtp/xtp.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as processOptionSelectors from 'data/processOption/processOption.selectors';
import * as diameterSelectors from 'data/diameter/diameter.selectors';
import * as cspConfigSelectors from 'data/cspConfig/cspConfig.selectors';
import * as cspConfigActions from 'data/cspConfig/cspConfig.actions';
import { TabProp } from 'shared/multi-tab/MultiTab';
import { showError } from 'shared/toaster';
import { DistributorConfigurationTableData } from 'data/distributorConfiguration/distributorConfiguration.types';
import {
  adaptToForm,
  adaptToCopy,
  adaptDeliveryViaToSubmit
} from 'data/accessFunction/accessFunction.adapter';
import AccessFunctionForm from 'routes/AccessFunction/components/AccessFunctionForm';
import { FieldUpdateHandlerParam } from 'shared/form';
import { ACCESS_FUNCTION_SECURITY_INFO_FILTER } from 'data/securityInfo/securityInfo.types';
import { useAccessFunctionFieldCreateNew } from 'routes/AccessFunction/hooks/useAccessFunctionFieldCreateNew';
import { adaptTemplate5GForGRExisting } from 'routes/AccessFunction/utils/handle5gPOIsforGR';
import { adapt4GAfsForGRToSubmit, adaptTemplate4GFieldsForGRExisting } from 'routes/AccessFunction/utils/handle4gPOIsforGR';
import {
  adaptTemplateOptions,
  adaptTemplateVisibilityMode,
  adaptLocationField,
  adaptSecurityInfoField,
  adaptTemplateForInterfacesFieldUpdate,
  adaptTemplateInterfaceFields,
  adaptTemplate5GFields,
  adaptTimezoneField,
  adaptTemplateLabelsForDeployment,
  adaptNetTypeFiled,
  adaptTemplateX2Interface,
  adaptDiameterField,
  adaptTemplateInterfaceAxsgp,
  adaptTemplateInterfaceForAXSGPModel,
  adaptTemplateDeliveryViaField,
  adaptDeliveryViaField,
  adaptCcdestipField,
  adaptTemplatesBasedOnAppSettings,
  adaptTemplateForNokiaCMG,
  adaptTemplateForNokiaCMGU,
  adaptTemplateForNokiaSMFAdmin,
  adaptTemplateForETSI103221,
  adaptTemplateAdvancedSettingForEricssonWebService,
  adaptTemplateMCTypeField,
  adaptTemplateForUtimaco,
  adaptTemplateForUtimacoMCTypeChange,
  adaptTemplateForInterfacesTabChange,
  adaptTemplateDeliveryFormatForEricssonLiims,
  adaptTemplateDeliveryFormatField,
  adaptTemplateForEricssonNE,
  adaptTemplateInterfaceForDeliveryFormat,
  adaptTemplateForAMFIdentifierAssociation,
} from '../../utils/accessFunctionTemplate.adapter';
import {
  handleModelFieldUpdateForNokiaCMG,
  handleModelFieldUpdateForMokiaCMGU
} from '../../utils/handleModelFieldUpdate';
import getDistributorConfigurationDataToSubmit from '../../utils/getDistributorConfigurationDataToSubmit';
import getAccessFunctionDataToSubmit from '../../utils/getAccessFunctionDataToSubmit';
import getIpAssignmentDataToSubmit from '../../utils/getIpAssignmentDataToSubmit';
import updateDistributorConfigs from '../../utils/updateDistributorConfigs';
import { createAccessFunctionBasedOnTemplate } from '../../utils/accessFunctionTemplate.factory';
import { handleInterfacesFieldUpdate } from '../../utils/accessFunctionInterfaceTabFieldUpdate';
import {
  handleDeliveryViaFieldUpdate,
  handleNodeNameFieldUpdate
} from '../../utils/handleDeliveryViaFieldUpdate';
import { useGetDistributorConfigurationsByFilter } from '../../hooks/useGetDistributorConfigurationsByFilter';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  selectedAccessFunction: accessFunctionSelectors.getSelectedAccessFunction(state),
  accessFunction: accessFunctionSelectors.getAccessFunction(state),
  filteredDistributorConfigurations: distributorConfigSelectors.getFilteredDistributorConfigurations(state),
  timezones: userSelectors.getTimezones(state),
  contacts: contactSelectors.getContacts(state),
  networks: networkSelectors.getNetworks(state),
  vpnConfigs: vpnConfigSelectors.getVpnConfigs(state),
  vpnConfigTemplates: vpnConfigTemplateSelectors.getVpnConfigTemplates(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  sshSecurityInfos: sshSecurityInfoSelectors.getSshSecurityInfos(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  enabledFeatures: supportedFeaturesSelectors.getEnabledFeatures(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  countries: locationSelectors.getCountryList(state),
  stateNameByCountry: locationSelectors.getStateListByCountry(state),
  filteredCities: locationSelectors.getFilteredCityList(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  accessFunctionByType: accessFunctionSelectors.getPoiAccessFunctionByType(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  xtps: xtpSelectors.getXtps(state),
  xdps: xdpSelectors.getXdps(state),
  processOptions: processOptionSelectors.getProcessOptions(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  is5gcVcpEnabled: supportedFeaturesSelectors.is5gcVcpEnabled(state),
  deployment: discoverySelectors.getDiscoveryDeployment(state),
  deliveryFunctionNetworkInterfaces: dfniSelectors.getDeliveryFunctionNetworkInterfaceList(state),
  isLocationRequired: discoverySelectors.isLocationRequired(state),
  diameter: diameterSelectors.getDiameter(state),
  isNplusKGREnabled: supportedFeaturesSelectors.isNplusKGREnabled(state),
  isMultitenantEnabled: supportedFeaturesSelectors.isMultitenantEnabled(state),
  cspConfigs: cspConfigSelectors.getCspConfigList(state),
  isIdentifierAssociationExtensionsEnabled: discoverySelectors.isIdentifierAssociationExtensionsEnabled(state),
});

const mapDispatch = {
  getAccessFunctionWithViewOptions: actions.getAccessFunctionWithViewOptions,
  getDistributorConfigurationsByFilter: distributorConfigActions.getDistributorConfigurationsByFilter,
  createAccessFunction: actions.createAccessFunction,
  goToAccessFunctionList: actions.goToAccessFunctionList,
  getTimezones: userActions.getTimezones,
  getContacts: contactActions.getAllContacts,
  getNetworks: networkActions.getNetworks,
  getVpnConfigs: vpnConfigActions.getVpnConfigs,
  getVpnConfigTemplates: vpnConfigTemplateActions.GetVpnConfigTemplates,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  getSshSecurityInfos: sshSecurityInfoActions.getSshSecurityInfos,
  fetchLocationList: locationActions.fetchLocationList,
  fetchLocationByFilterWithViewCities: locationActions.fetchLocationByFilterWithViewCities,
  getXtps: xtpActions.getXtps,
  getXdps: xdpActions.getXdps,
  getDfnis: dfniActions.getDeliveryFunctionNetworkInterfaceList,
  getCspConfigs: cspConfigActions.getCspConfigList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => AccessFunctionFormData
  getIpAssignmentValues: () => IpAddressFormData[]
  getManualEntryValues: () => ManualEntryFormData
}

export const AccessFunctionCopy: FunctionComponent<Props> = ({
  deliveryFunctionId,
  selectedAccessFunction,
  accessFunction,
  filteredDistributorConfigurations,
  timezones,
  contacts,
  networks,
  securityInfos,
  sshSecurityInfos,
  vpnConfigs,
  vpnConfigTemplates,
  history,
  enabledAccessFunctions,
  enabledFeatures,
  isAuditEnabled,
  userId,
  userName,
  countries,
  stateNameByCountry,
  filteredCities,
  accessFunctions,
  accessFunctionByType,
  accessFunctionTemplates,
  xtps,
  xdps,
  processOptions,
  xcipioNodes,
  is5gcVcpEnabled,
  isNplusKGREnabled,
  isMultitenantEnabled,
  deployment,
  isLocationRequired,
  diameter,
  cspConfigs,
  isIdentifierAssociationExtensionsEnabled,
  createAccessFunction,
  goToAccessFunctionList,
  getAccessFunctionWithViewOptions,
  getTimezones,
  getContacts,
  getNetworks,
  getVpnConfigs,
  getVpnConfigTemplates,
  getSecurityInfos,
  getSshSecurityInfos,
  getDistributorConfigurationsByFilter,
  fetchLocationList,
  fetchLocationByFilterWithViewCities,
  getXtps,
  deliveryFunctionNetworkInterfaces,
  getXdps,
  getCspConfigs,
}: Props) => {
  const [templates, setTemplates] = useState<{ [key: string]: FormTemplate }>({});
  const [template, setTemplate] = useState<FormTemplate>();
  const [data, setData] = useState<Partial<AccessFunctionFormData>>({});
  const [distributorConfigsTableData, setDistributorConfigsTableData] =
    useState<DistributorConfigurationTableData[]>([]);
  const accessFunctionFormRef = useRef<Ref>(null);
  const accessFunctionId = selectedAccessFunction[0]?.id;
  const { t } = useTranslation();

  const [handleFieldCreateNew] = useAccessFunctionFieldCreateNew(
    (param, formSection, tabId) => handleFieldUpdate(param, formSection, tabId),
  );

  useEffect(() => {
    if (accessFunctionId) {
      getAccessFunctionWithViewOptions(deliveryFunctionId, accessFunctionId);
    }
  }, [accessFunctionId, deliveryFunctionId, getAccessFunctionWithViewOptions]);

  useEffect(() => {
    getTimezones();
    getContacts(deliveryFunctionId);
    getNetworks(deliveryFunctionId);
    getSecurityInfos(deliveryFunctionId);
    getSshSecurityInfos(deliveryFunctionId);
    getVpnConfigs(deliveryFunctionId);
    getVpnConfigTemplates(deliveryFunctionId);
    fetchLocationList();
    getXtps(deliveryFunctionId);
    getXdps(deliveryFunctionId);
    getCspConfigs(deliveryFunctionId);
  }, [
    deliveryFunctionId,
    getTimezones,
    getContacts,
    getNetworks,
    getSecurityInfos,
    getSshSecurityInfos,
    getVpnConfigs,
    getVpnConfigTemplates,
    fetchLocationList,
    getXtps,
    getXdps,
    getCspConfigs,
  ]);

  useEffect(() => {

    let availableTemplates = adaptTemplateOptions(
      accessFunctionTemplates,
      countries,
      timezones,
      contacts,
      networks,
      securityInfos,
      sshSecurityInfos,
      xtps,
      xdps,
      processOptions,
      enabledAccessFunctions,
      xcipioNodes,
      accessFunctions,
      deliveryFunctionNetworkInterfaces,
      enabledFeatures,
      cspConfigs,
    );

    availableTemplates = adaptTemplatesBasedOnAppSettings(
      availableTemplates,
      isLocationRequired,
      isIdentifierAssociationExtensionsEnabled,
    )

    availableTemplates = adaptTemplateX2Interface(
      availableTemplates,
      enabledFeatures,
    )

    setTemplates(availableTemplates);
  }, [
    accessFunctionTemplates,
    countries,
    timezones,
    contacts,
    networks,
    securityInfos,
    sshSecurityInfos,
    accessFunctions,
    xtps,
    xdps,
    processOptions,
    enabledAccessFunctions,
    xcipioNodes,
    deliveryFunctionNetworkInterfaces,
    enabledFeatures,
    cspConfigs,
  ]);

  useEffect(() => {
    if (accessFunction != null && templates != null) {
      let formData = data as AccessFunctionFormData;
      if (formData.tag == null) {
        formData = adaptToForm(accessFunction);
        formData = adaptToCopy(formData);
        setData(formData);
      }

      let templateData = adaptTemplateVisibilityMode(templates[formData.tag], 'copy');

      const securityInfoFilter = ACCESS_FUNCTION_SECURITY_INFO_FILTER[formData.tag] ?? {};
      templateData = adaptSecurityInfoField(templateData, securityInfos, securityInfoFilter);
      templateData = adaptTemplate5GFields(templateData, is5gcVcpEnabled);
      templateData = adaptTemplate4GFieldsForGRExisting(templateData, formData, isNplusKGREnabled, xcipioNodes, networks, templates);
      templateData = adaptTemplateLabelsForDeployment(templateData, deployment);
      templateData = adaptTimezoneField(templateData, timezones, formData);
      templateData = adaptNetTypeFiled(templateData, formData);
      templateData = adaptDiameterField(templateData, diameter, formData);
      templateData = adaptTemplateDeliveryFormatForEricssonLiims(templateData, formData);
      templateData = adaptTemplateInterfaceFields(templateData, formData, xcipioNodes);
      templateData = adaptTemplateInterfaceForAXSGPModel(templateData, 'model', formData.model as string);
      templateData = adaptTemplateForUtimaco(templateData)
      templateData = adaptTemplateForUtimacoMCTypeChange(templateData, formData)

      const { country, stateName } = formData;

      if (country) {
        const stateNames = stateNameByCountry[country]?.map(({ stateName: entry }) => entry) ?? [];
        templateData = adaptLocationField(templateData, stateNames, 'stateName');
      }

      if (country && stateName) {
        const { stateCode } = stateNameByCountry[country]?.find(({ stateName: entry }) => entry === stateName) ?? {};
        fetchLocationByFilterWithViewCities(`country eq '${country}' and stateCode eq '${stateCode}'`);
      }

      templateData = adaptDeliveryViaField(templateData, formData);
      templateData = adaptCcdestipField(templateData, formData, xcipioNodes);
      templateData = adaptTemplate5GForGRExisting(templateData, formData, xcipioNodes, networks, accessFunctions, templates);
      templateData = adaptTemplateForNokiaCMG(templateData, formData?.model, enabledAccessFunctions?.NOKIA_CMG);
      templateData = adaptTemplateForNokiaCMGU(templateData, enabledAccessFunctions?.NOKIA_CMG_U, formData?.model);
      templateData = adaptTemplateForNokiaSMFAdmin(templateData, formData?.model, enabledAccessFunctions?.NOKIA_SMF_ADMIN);
      templateData = adaptTemplateForETSI103221(isIdentifierAssociationExtensionsEnabled, templateData, enabledAccessFunctions?.ETSI103221, formData?.model);
      templateData = adaptTemplateDeliveryFormatField(templateData, 'dataInterfaces', 'deliveryFormat', formData?.deliveryFormat ?? '')
      templateData = adaptTemplateForEricssonNE(templateData, undefined, formData);
      templateData = adaptTemplateForAMFIdentifierAssociation(isIdentifierAssociationExtensionsEnabled, templateData, formData?.model)

      // get appprotocolver
      if (formData && formData.provisioningInterfaces) {
        const appprotocolver = formData?.provisioningInterfaces[0]?.version;
        templateData = adaptTemplateAdvancedSettingForEricssonWebService(appprotocolver, templateData);
      }
      setTemplate(templateData);
    }
  }, [
    accessFunction,
    securityInfos,
    stateNameByCountry,
    templates,
    fetchLocationByFilterWithViewCities,
    accessFunctionByType,
    is5gcVcpEnabled,
    timezones,
    deployment,
  ]);

  useGetDistributorConfigurationsByFilter(deliveryFunctionId, template, getDistributorConfigurationsByFilter);

  useEffect(() => {
    const tableData = distributorConfigAdapters.adaptToTableData(filteredDistributorConfigurations);
    setDistributorConfigsTableData(tableData);
  }, [filteredDistributorConfigurations]);

  useEffect(() => {
    if (filteredCities.length > 0 && data?.country && data?.stateName) {
      const templateData = adaptLocationField(template, filteredCities, 'city');
      setTemplate(templateData);
    }
  }, [filteredCities]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFieldUpdate = (
    { name: fieldName, value }: FieldUpdateHandlerParam,
    formSection: AccessFunctionFormSection,
    interfaceId?: string | number,
  ) => {
    let clonedData = cloneDeep(data);

    if (formSection === 'general' || formSection === 'systemInformation') {
      if (fieldName === 'country') {
        const stateNames = stateNameByCountry[value]?.map(({ stateName }) => stateName) ?? [];

        let templateData = adaptLocationField(template, stateNames, 'stateName');
        templateData = adaptLocationField(templateData, [], 'city');

        setData({
          ...clonedData,
          [fieldName]: value,
          stateName: '',
          city: '',
        });
        setTemplate(templateData);

      } else if (fieldName === 'stateName') {
        const { country = '' } = clonedData ?? {};
        const { stateCode } = stateNameByCountry[country]?.find(({ stateName }) => stateName === value) ?? {};

        if (country && stateCode) {
          fetchLocationByFilterWithViewCities(`country eq '${country}' and stateCode eq '${stateCode}'`);
        }

        const templateData = adaptLocationField(template, [], 'city');

        setData({
          ...clonedData,
          [fieldName]: value,
          city: '',
        });
        setTemplate(templateData);

      } else if (fieldName === 'name') {
        setData({
          ...clonedData,
          [fieldName]: value,
        });

      } else if (fieldName === 'contentDeliveryVia') {
        const adaptedTemplate = adaptTemplateDeliveryViaField(template, formSection, clonedData, fieldName, value);
        clonedData = handleDeliveryViaFieldUpdate(adaptedTemplate, clonedData, value);
        clonedData = handleNodeNameFieldUpdate(clonedData, value, formSection, fieldName);

        setData({
          ...clonedData,
          [fieldName]: value,
        });
        setTemplate(adaptedTemplate);

      } else if (fieldName === 'model') {
        let adaptedTemplate = adaptTemplateForNokiaCMG(template, value);
        adaptedTemplate = adaptTemplateForNokiaCMGU(adaptedTemplate, enabledAccessFunctions?.NOKIA_CMG_U, value);
        adaptedTemplate = adaptTemplateForNokiaSMFAdmin(adaptedTemplate, value);
        adaptedTemplate = adaptTemplateForETSI103221(isIdentifierAssociationExtensionsEnabled, adaptedTemplate, enabledAccessFunctions?.ETSI103221, value);

        if (template?.key === 'NOKIA_CMG' && clonedData?.provisioningInterfaces?.length) {
          clonedData.provisioningInterfaces[0].pfcplisharedkey = '';
        }

        clonedData = handleModelFieldUpdateForNokiaCMG(clonedData, template, value);
        clonedData = handleModelFieldUpdateForMokiaCMGU(clonedData, template, value);

        setData({
          ...clonedData,
          [fieldName]: value,
        });
        setTemplate(adaptedTemplate);

      } else if (fieldName === 'version') {
        let adaptedTemplate = adaptTemplateInterfaceAxsgp(template, fieldName, value, templates);
        adaptedTemplate = adaptTemplateForNokiaCMG(adaptedTemplate, clonedData?.model);
        adaptedTemplate = adaptTemplateAdvancedSettingForEricssonWebService(value, adaptedTemplate);

        if ((template?.key === 'NOKIA_CMG') && clonedData.provisioningInterfaces?.length) {
          clonedData.provisioningInterfaces[0].version = value;
        }

        if ((template?.key === 'ERICSSON_LIMS' || template?.key === 'ERICSSON_LIMS_CORBA')
          && clonedData.provisioningInterfaces?.length) {
          clonedData.provisioningInterfaces[0].appprotocolver = value;
        }

        setData({
          ...clonedData,
          [fieldName]: value,
        });
        setTemplate(adaptedTemplate);

      } else {
        setData({
          ...clonedData,
          [fieldName]: value,
        });
      }

    } else if (formSection === 'provisioningInterfaces'
      || formSection === 'dataInterfaces'
      || formSection === 'contentInterfaces'
      || formSection === 'monitoringCenters'
      || formSection === 'territories'
      || formSection === 'eimsNetworkElement'
      || formSection === 'diameter'
      || formSection === 'manualEntry'
      || formSection === 'ipAssignment'
      || formSection === 'fileImportExport'
      || formSection === 'usnbdAgencies'
      || formSection === 'usnbdCallDataChannels') {
      let adaptedTemplate = cloneDeep(template);

      clonedData = handleInterfacesFieldUpdate(
        adaptedTemplate,
        clonedData,
        formSection,
        interfaceId as string,
        fieldName,
        value,
        xcipioNodes,
        undefined,
        accessFunctions
      );

      adaptedTemplate = adaptTemplateForInterfacesFieldUpdate(
        formSection,
        clonedData,
        adaptedTemplate,
        fieldName,
        value,
        xcipioNodes,
        networks,
        accessFunctions,
        templates
      );

      if ((formSection === 'contentInterfaces' && fieldName === 'contentDeliveryVia') ||
        (formSection === 'dataInterfaces' && fieldName === 'dataDeliveryVia')) {
        adaptedTemplate = adaptTemplateDeliveryViaField(adaptedTemplate, formSection, clonedData, fieldName, value);
        clonedData = handleDeliveryViaFieldUpdate(adaptedTemplate, clonedData, value);
      }

      if (template?.key === 'SIEMENS_LIMS' && formSection === 'dataInterfaces' && fieldName === 'mctype') {
        adaptedTemplate = adaptTemplateMCTypeField(adaptedTemplate, formSection, fieldName, value)
      }

      adaptedTemplate = adaptTemplateDeliveryFormatField(adaptedTemplate, formSection, fieldName, value);

      clonedData = handleNodeNameFieldUpdate(clonedData, value, formSection, fieldName);

      setData(clonedData);
      if (adaptedTemplate) {
        adaptedTemplate.formId = Date.now().toString();
      }
      setTemplate(adaptedTemplate);
    }
  };

  const handleDistributorUpdate = (distributorConfigTableData: DistributorConfigurationTableData) => {
    const updatedDistributorConfigsTableData = updateDistributorConfigs(
      distributorConfigTableData,
      distributorConfigsTableData,
    );
    setDistributorConfigsTableData(updatedDistributorConfigsTableData);
  };

  const handleClickSave = () => {
    const isValid = accessFunctionFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    let formData = accessFunctionFormRef?.current?.getValues();
    if (!formData) {
      return;
    }

    if (isMultitenantEnabled && !formData.cspid) {
      showError(t('accessFunction.multitenantCspRequired'));
      return;
    }

    formData = adaptDeliveryViaToSubmit(formData, data, enabledAccessFunctions);
    formData = adapt4GAfsForGRToSubmit(formData, data);

    const accessFunctionBody = getAccessFunctionDataToSubmit(
      formData,
      null,
      'CREATE',
      undefined,
      undefined,
      diameter
    );

    const distributorConfigurationBodies = getDistributorConfigurationDataToSubmit(
      distributorConfigsTableData,
      filteredDistributorConfigurations,
      isAuditEnabled,
      userId,
      userName,
    );

    const manualEntryBody = accessFunctionFormRef?.current?.getManualEntryValues();
    const ipAssignmentBody: IpAddressFormData[] | undefined = accessFunctionFormRef?.current?.getIpAssignmentValues();

    const ipAssignmentsBody = [];

    if (manualEntryBody && ipAssignmentBody) {
      if (ipAssignmentBody[0].ipAddr !== '' && ipAssignmentBody[0].prefix) {
        const firstIpAddress = {
          ipAddr: ipAssignmentBody[0].ipAddr,
          prefix: ipAssignmentBody[0].prefix
        }

        const firstRequest = {
          appId: accessFunctionBody.data.name,
          ipAddress: `${firstIpAddress.ipAddr}/${firstIpAddress.prefix}`,
          forceOp: manualEntryBody.forceOp ? 'YES' : 'NO',
          servId: manualEntryBody.servId,
          servType: manualEntryBody.servType
        }

        const firstRequestBody = getIpAssignmentDataToSubmit(
          firstRequest,
          isAuditEnabled,
          userId,
          userName,
          'UPDATE'
        );
        ipAssignmentsBody.push(firstRequestBody);
      }

      if (ipAssignmentBody[1].ipAddr !== '' && ipAssignmentBody[1].prefix) {
        const secondIpAddress = {
          ipAddr: ipAssignmentBody[1].ipAddr,
          prefix: ipAssignmentBody[1].prefix
        }

        const secondRequest = {
          appId: accessFunctionBody.data.name,
          ipAddress: `${secondIpAddress.ipAddr}/${secondIpAddress.prefix}`,
          forceOp: manualEntryBody.forceOp ? 'YES' : 'NO',
          servId: manualEntryBody.servId,
          servType: manualEntryBody.servType
        }

        const secondRequestBody = getIpAssignmentDataToSubmit(
          secondRequest,
          isAuditEnabled,
          userId,
          userName,
          'UPDATE'
        );
        ipAssignmentsBody.push(secondRequestBody);
      }
    }

    createAccessFunction(deliveryFunctionId, accessFunctionBody, history, distributorConfigurationBodies, ipAssignmentsBody);
  };

  const handleCancel = () => goToAccessFunctionList(history);

  const handleTabAdd = (formSectionName: AccessFunctionInterfaceFormSectionType) => {
    const clonedData = cloneDeep(data);
    if (template?.key === 'SIEMENS_LIMS') {
      let adaptedTemplate: FormTemplate | undefined = cloneDeep(template)
      adaptedTemplate = adaptTemplateMCTypeField(adaptedTemplate, 'dataInterfaces', 'mctype')
      setTemplate(adaptedTemplate);
    }

    const newData = createAccessFunctionBasedOnTemplate(template, true);
    const newTabData = (newData[formSectionName] as AccessFunctionInterfaceFormData[])[0];

    if (formSectionName === 'provisioningInterfaces' && clonedData?.provisioningInterfaces?.length) {
      if (newTabData && Object.prototype.hasOwnProperty.call(newTabData, 'mdf2NodeNameMultiSelect')) {
        newTabData.mdf2NodeNameMultiSelect = clonedData.provisioningInterfaces[0].mdf2NodeNameMultiSelect;
      }
      if (newTabData && Object.prototype.hasOwnProperty.call(newTabData, 'mdf3NodeNameMultiSelect')) {
        newTabData.mdf3NodeNameMultiSelect = clonedData.provisioningInterfaces[0].mdf3NodeNameMultiSelect;
      }
    }

    if (clonedData[formSectionName] !== undefined) {
      (clonedData[formSectionName] as AccessFunctionInterfaceFormData[]).push(newTabData);
    } else {
      clonedData[formSectionName] = [newTabData];
    }
    setData(clonedData);
  };

  const handleTabRemove = (tabId: string | number, formSectionName: AccessFunctionInterfaceFormSectionType) => {
    const clonedData = cloneDeep(data);
    const clonedTemplate = cloneDeep(template);

    clonedData[formSectionName] = clonedData[formSectionName]?.filter(({ id }) => id !== tabId);

    if (clonedData?.tag === 'PCRFIPQ' && clonedData?.provisioningInterfaces?.length === 1 && clonedTemplate != null) {
      clonedTemplate.formId = Date.now().toString();
      clonedData.provisioningInterfaces[0].rolepriority = 'PRIMARY';
      setTemplate(clonedTemplate);
    }

    setData(clonedData);
  };

  if (!template || !data) {
    return null;
  }

  const handleTabChangeDeliveryFormat = (deliveryFormat: string, adaptedTemplate: FormTemplate | undefined) => {
    if (adaptedTemplate) {
      const affectedFields = (adaptedTemplate.dataInterfaces as FormTemplateSection).fields.deliveryFormat.affectedFieldsOnChange;
      adaptTemplateInterfaceForDeliveryFormat(affectedFields, adaptedTemplate.dataInterfaces as FormTemplateSection, deliveryFormat, (template as FormTemplate).key);
    }
  };

  const handleTabChange = (tab: TabProp, formSectionName: AccessFunctionInterfaceFormSectionType) => {
    if (template?.key === 'COGNYTE' ||
      template?.key === 'ERICSSON_LIMS' ||
      template?.key === 'ERICSSON_LIMS_CORBA' ||
      template?.key === 'SIEMENS_LIMS' ||
      template?.key === 'VRNT_SG' ||
      template?.key === 'VIETTEL_MD') {
      let clonedData = cloneDeep(data);
      let adaptedTemplate: FormTemplate | undefined = cloneDeep(template);
      adaptedTemplate.formId = Date.now().toString();
      if (tab.panel.props.defaults.deliveryFormat) {
        clonedData = handleInterfacesFieldUpdate(
          template,
          clonedData,
          formSectionName,
          tab.id as string,
          'deliveryFormat',
          tab.panel.props.defaults.deliveryFormat,
          xcipioNodes,
        );

        handleTabChangeDeliveryFormat(tab.panel.props.defaults.deliveryFormat, adaptedTemplate);
      }
      if (tab.panel.props.defaults.mctype) {
        adaptedTemplate = adaptTemplateMCTypeField(adaptedTemplate, 'dataInterfaces', 'mctype', tab.panel.props.defaults.mctype)
      }

      setData(clonedData);
      setTemplate(adaptedTemplate);
    }
  };

  return (
    <AccessFunctionForm
      title={t('accessFunction.copy.title')}
      isEditing
      ref={accessFunctionFormRef}
      data={data}
      accessFunctionByType={accessFunctionByType}
      enabledAccessFunctions={enabledAccessFunctions}
      distributorConfigs={distributorConfigsTableData}
      vpnConfigs={vpnConfigs}
      vpnConfigTemplates={vpnConfigTemplates}
      template={template}
      networks={networks}
      is5gcVcpEnabled={is5gcVcpEnabled}
      onFieldUpdate={handleFieldUpdate}
      onDistributorUpdate={handleDistributorUpdate}
      onClickSave={handleClickSave}
      onClickCancelClose={handleCancel}
      onTabAdd={handleTabAdd}
      onTabRemove={handleTabRemove}
      onFieldCreateNew={handleFieldCreateNew}
      onTabChange={handleTabChange}
      mode="copy"
      xcipioNodes={xcipioNodes}
    />
  );
};

export default withRouter(connector(AccessFunctionCopy));
